import React, { useCallback, useMemo, useState } from 'react';
import { Input, Menu, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { fetchAccountByNumber } from '../../utils/accountStructure/accountStructureUtils';
import './styles.scss';

const SECTION_KEYS = ['BS', 'BSA', 'BSL', 'BSE', 'PL', 'CF', 'DRVE'];
const GROUP_KEY_PREFIX = 'group:';

const accountLabel = (account) =>
    account.name ? `${account.account_number} – ${account.name}` : account.account_number;

const isAccountRow = (account) => account.row_type !== 'separator' && account.account_number != null;

/**
 * Keeps accounts whose number or name contains the query, plus the parents
 * needed to reach them. A matching parent keeps its whole subtree.
 */
const filterAccounts = (data = [], query) => {
    if (!query) return data;

    return data.filter(isAccountRow).reduce((kept, account) => {
        const matches =
            String(account.account_number).toLowerCase().includes(query) ||
            String(account.name ?? '').toLowerCase().includes(query);

        if (matches) {
            kept.push(account);
            return kept;
        }

        const children = filterAccounts(account.children, query);
        if (children.length > 0) kept.push({ ...account, children });
        return kept;
    }, []);
};

/**
 * Menu of the account structure for picking an account.
 *
 * @param inline  render as a searchable inline tree that expands/collapses on click
 *                (stays open until collapsed) instead of hover fly-outs. Parent accounts
 *                are then selected via their first child entry, so expanding never selects.
 */
const AccountStructureMenu = ({ accountStructure, handleSelect, inline = false }) => {
    const [search, setSearch] = useState('');
    const [userOpenKeys, setUserOpenKeys] = useState([]);
    const query = inline ? search.trim().toLowerCase() : '';

    const handleMenuClick = useCallback(
        (e) => {
            if (SECTION_KEYS.includes(e.key) || String(e.key).startsWith(GROUP_KEY_PREFIX)) {
                //do not consider the balancesheet, profitloss and cashflow keys...
                return;
            }

            const account = {};
            fetchAccountByNumber(e.key, account, accountStructure);

            handleSelect(account['account']);
        },
        [accountStructure, handleSelect],
    );

    const loop = useCallback(
        (data = []) =>
            data.filter(isAccountRow).map((account) => {
                const hasChildren = Array.isArray(account.children) && account.children.length > 0;

                if (!hasChildren) {
                    return { key: account.account_number, label: accountLabel(account) };
                }

                if (inline) {
                    return {
                        key: GROUP_KEY_PREFIX + account.account_number,
                        label: accountLabel(account),
                        children: [
                            {
                                key: account.account_number,
                                label: `Insert ${account.account_number} (total)`,
                                className: 'account-structure-menu-total',
                            },
                            ...loop(account.children),
                        ],
                    };
                }

                return {
                    key: account.account_number,
                    label: accountLabel(account),
                    children: loop(account.children),
                    onTitleClick: handleMenuClick,
                };
            }),
        [handleMenuClick, inline],
    );

    const items = useMemo(() => {
        if (!accountStructure) return [];

        const sections = [
            {
                key: 'BS',
                label: 'Balance Sheet',
                children: [
                    { key: 'BSA', label: 'Assets', accounts: accountStructure.balancesheet.assets },
                    { key: 'BSL', label: 'Liabilities', accounts: accountStructure.balancesheet.liabilities },
                    { key: 'BSE', label: 'Equity', accounts: accountStructure.balancesheet.equities },
                ],
            },
            { key: 'PL', label: 'Profit & Loss', accounts: accountStructure.profitloss },
            { key: 'CF', label: 'Cash flow', accounts: accountStructure.cashflow },
            { key: 'DRVE', label: 'Drivers', accounts: accountStructure.drivers },
        ];

        const build = (section) => {
            if (section.children) {
                const children = section.children.map(build).filter(Boolean);
                return children.length > 0 || !query ? { key: section.key, label: section.label, children } : null;
            }

            const children = loop(filterAccounts(section.accounts, query));
            return children.length > 0 || !query ? { key: section.key, label: section.label, children } : null;
        };

        return sections.map(build).filter(Boolean);
    }, [accountStructure, loop, query]);

    // While searching, open every branch that leads to a match.
    const searchOpenKeys = useMemo(() => {
        if (!query) return [];
        const keys = [];
        const walk = (list) =>
            list.forEach((item) => {
                if (item.children) {
                    keys.push(item.key);
                    walk(item.children);
                }
            });
        walk(items);
        return keys;
    }, [items, query]);

    if (inline) {
        return (
            <div className="account-structure-menu-inline">
                <Input
                    allowClear
                    size="small"
                    prefix={<SearchOutlined />}
                    placeholder="Search account number or name"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="account-structure-menu-search"
                />
                <div className="account-structure-menu-tree">
                    {items.length === 0 ? (
                        <Typography.Text type="secondary" style={{ display: 'block', padding: '8px' }}>
                            No accounts match "{search.trim()}"
                        </Typography.Text>
                    ) : (
                        <Menu
                            mode="inline"
                            onClick={handleMenuClick}
                            items={items}
                            inlineIndent={16}
                            selectable={false}
                            openKeys={query ? searchOpenKeys : userOpenKeys}
                            onOpenChange={(keys) => {
                                if (!query) setUserOpenKeys(keys);
                            }}
                        />
                    )}
                </div>
            </div>
        );
    }

    return <Menu onClick={handleMenuClick} items={items} inlineIndent={0} />;
};

export default AccountStructureMenu;
